import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { applyNodeChanges as rfApplyNodeChanges, applyEdgeChanges as rfApplyEdgeChanges } from '@xyflow/react';
import type { NodeChange, EdgeChange } from '@xyflow/react';
import { uid } from 'uid';

import { selectApiLst } from 'services/ApiCommon';
import type {
  CommonState,
  ModelInfo,
  EntityInfo,
  FocusInfo,
  DictionaryState,
  LiveParticipation,
  LoadingProgress,
  LocationHistoryItem,
  DiagramMode,
  RelType,
  ViewMode,
  SelectionMode,
  LegacyWorkflowMap,
  LegacyRefineResult,
  AnalysisResults,
  ApiListItem,
  initialEntityInfo,
  initialFocusInfo,
  initialModelInfo,
} from '../../types';
import type { IdentifierChangedInfo } from '../../types/models/relationship.types';
import type { Viewport, AttributeGroup } from '../../types/models/entity.types';

// ============================================================
// Async Thunks
// ============================================================

export const loadApiList = createAsyncThunk(
  'base/common/loadApiList',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await selectApiLst();
      dispatch(commonSlice.actions.setApiList((response as { apiLst: unknown[] }).apiLst));
      return (response as { apiLst: unknown[] }).apiLst;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// ============================================================
// Payload Types
// ============================================================

interface ModelKeyPayload {
  modelKey: string;
}

interface SetWorkflowStepPayload {
  modelKey: string;
  result: Record<string, unknown>;
}

interface SetCurrentStepPayload {
  modelKey: string;
  step: number;
}

interface SetModelConfigPayload {
  modelKey?: string;
  mdlrmdlId?: string;
  modelConfig: Record<string, unknown>;
}

interface RenameModelPayload {
  modelKey: string;
  newModelName: string;
}

interface RenameProjectPayload {
  oldProjectNm: string;
  newProjectNm: string;
}

interface ModelChangedPayload {
  modelKey: string;
  changed: boolean;
}

interface EdgeUpdatePayload {
  id: string;
  [key: string]: unknown;
}

interface EdgeItemPayload {
  entityId: string;
  edgeId: string;
}

interface MergeItemsPayload {
  entityId: string;
  items: unknown[];
}

interface ReplaceAttrItemsPayload {
  entityId: string;
  items: unknown[];
}

interface QualityResultsPayload {
  modelKey: string;
  entityId?: string;
  qualityByAttr: Record<string, Record<string, unknown>>;
}

interface EntityQualityPayload {
  entityId: string;
  qualityByAttr: Record<string, Record<string, unknown>>;
}

interface AnalysisResultPayload {
  modelKey: string;
  result: Record<string, unknown>;
}

interface UpdateModelAttrsByIdPayload {
  modelKey: string;
  attrs: unknown[];
}

interface ProjectTermPayload {
  projectKey?: string;
  projectResult: Record<string, unknown>;
  modelResults?: Array<{ modelKey: string } & Record<string, unknown>>;
}

interface SetNamingRuleStep1Payload {
  modelKey: string;
  result: {
    namingRules?: Record<string, unknown[]>;
    [key: string]: unknown;
  };
  append?: boolean;
}

interface LLMSettingsPayload {
  modelKey: string;
  settings: { model: string | null; skipCache: boolean };
}

interface ExclusiveGroupPayload {
  edgeIds: string[];
  targetEntityId: string;
  notation?: string;
  mergeAttributes?: boolean;
  mergedAttributeName?: string;
}

interface UpdateExclusiveGroupPayload {
  id: string;
  [key: string]: unknown;
}

interface ExclusiveNotationPayload {
  groupId: string;
  notation: string;
}

interface EdgeIdUpdatePayload {
  oldEdgeId: string;
  newEdgeId: string;
}

interface SubtypeGroupPayload {
  edgeIds: string[];
  sourceEntityId: string;
  notation?: string;
  isExclusive?: boolean;
  isComplete?: boolean;
  discriminator?: string;
}

interface SubtypeOptionsPayload {
  groupId: string;
  isExclusive?: boolean;
  isComplete?: boolean;
  discriminator?: string;
  notation?: string;
}

interface EntityPhysicalNameUpdate {
  entityId: string;
  newPhysicalName: string;
}

interface ModelConfigTabPayload {
  modelId: string;
  tabIndex: number;
  newTabData: Record<string, unknown>;
}

interface ModelConfigSectionPayload {
  modelId: string;
  tabIndex: number;
  sectionIndex: number;
  newSectionData: Record<string, unknown>;
}

interface MultipleAttrItemsPayload {
  updates: Array<{ entityId: string; items: unknown[] }>;
}

interface DeselectItemsPayload {
  entityId: string;
}

// ============================================================
// localStorage 토글 상태 복원
// ============================================================
const TOGGLE_STORAGE_KEY = 'datamodeler_toggles';

const loadToggles = (): { diagramMode?: string; autoAdjustEdges?: boolean } => {
  try {
    const saved = localStorage.getItem(TOGGLE_STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch { return {}; }
};

const saveToggle = (key: string, value: unknown) => {
  try {
    const current = loadToggles();
    localStorage.setItem(TOGGLE_STORAGE_KEY, JSON.stringify({ ...current, [key]: value }));
  } catch { /* ignore */ }
};

const savedToggles = loadToggles();

// ============================================================
// Initial State
// ============================================================

export const initialState: CommonState = {
  currentRouteKey: '',
  isUndo: true,
  isRedo: true,
  undoRedoBtn: '',

  entityInfo: {
    id: '',
    data: {
      entityId: '',
      entityName: '',
      entityDescription: '',
      physicalName: '',
    },
    entityType: '',
  },

  focusInfo: {
    focusArea: '',
    focusName: '',
    focusDescription: '',
  },

  modelInfo: {
    viewport: {},
    nodes: [],
    edges: [],
    key: '',
    modelName: '',
    modelDescription: '',
    projectNm: '',
    openMethod: '',
    attrs: [],
    mdlrmdlId: '',
    modelConfig: {},
    changed: false,
    exclusiveGroups: [],
    subtypeGroups: [],
  },

  multiModelData: {},
  activeModelKey: null,
  itemMenu: [],
  diagramMode: (savedToggles.diagramMode as DiagramMode) || 'L',
  relType: 'IR',
  viewMode: 'detailed',
  edgeLabelVisible: false,
  isDetailVisible: false,
  userLoggingMode: 'B',
  identifierChanged: {
    entityId: '',
    itemId: '',
    type: '',
  },
  viewport: null,

  dictionary: {
    words: [],
    terms: [],
    domainGroup: [],
    infotype: [],
    loading: false,
    error: null,
  },

  modelConfigs: [],
  toolbarLists: [],

  liveParticipation: {
    mdlrmdlId: '',
    shareGroupId: '',
    assignmentName: '',
  },
  liveModel: {},
  liveMdlrmdlId: '',
  lastModels: [],

  status: 'idle',
  error: null,
  apiList: [],
  apiListLoaded: false,
  appInitialized: false,
  persistLoaded: false,
  modelDataRestored: false,
  isModelLoading: false,
  modelLoadingProgress: { current: 0, total: 0 },
  isSaving: false,
  savingProgress: { current: 0, total: 0 },
  hoveredEdgeId: null,
  selectionMode: null,
  autoAdjustEdges: savedToggles.autoAdjustEdges ?? false,

  analysisResults: {},

  // 레거시 워크플로우 (daExtensionSlice로 이동됨)
  termCandidateWorkflow: {},
  entityRefineWorkflow: {},
  attributeRefineWorkflow: {},
  entityRefineResult: null,
  attributeRefineResult: null,

  locationHistory: {
    items: [],
    currentIndex: -1,
  },
};

// ============================================================
// Slice
// ============================================================

export const commonSlice = createSlice({
  name: 'base/common',
  initialState,

  extraReducers: (builder) => {
    builder
      .addCase(loadApiList.pending, (state) => {
        state.status = 'loading';
        state.apiListLoaded = false;
      })
      .addCase(loadApiList.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.apiList = (action.payload as unknown[]).map(item => item as ApiListItem);
        state.apiListLoaded = true;
      })
      .addCase(loadApiList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        state.apiListLoaded = false;
      });
  },

  reducers: {
    setPersistLoaded: (state, action: PayloadAction<boolean>) => {
      state.persistLoaded = action.payload;
    },
    setModelDataRestored: (state, action: PayloadAction<boolean>) => {
      state.modelDataRestored = action.payload;
    },

    setModelLoading: (state, action: PayloadAction<boolean>) => {
      state.isModelLoading = action.payload;
      if (!action.payload) {
        state.modelLoadingProgress = { current: 0, total: 0 };
      }
    },

    setModelLoadingProgress: (state, action: PayloadAction<LoadingProgress>) => {
      state.modelLoadingProgress = action.payload;
    },

    setSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload;
      if (!action.payload) {
        state.savingProgress = { current: 0, total: 0 };
      }
    },

    setSavingProgress: (state, action: PayloadAction<LoadingProgress>) => {
      state.savingProgress = action.payload;
    },

    setApiList: (state, action: PayloadAction<unknown[]>) => {
      state.apiList = action.payload.map(item => item as ApiListItem);
      state.apiListLoaded = true;
    },

    setLiveModel: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.liveModel = action.payload;
    },

    setLiveMdlrmdlId: (state, action: PayloadAction<string>) => {
      state.liveMdlrmdlId = action.payload;
    },

    setLiveParticipation: (state, action: PayloadAction<LiveParticipation>) => {
      state.liveParticipation = action.payload;
    },

    setToolbarLists: (state, action: PayloadAction<unknown[]>) => {
      state.toolbarLists = action.payload;
    },

    setViewport: (state, action: PayloadAction<Viewport | null>) => {
      state.viewport = action.payload;
    },

    setIdentifierChanged: (state, action: PayloadAction<IdentifierChangedInfo>) => {
      state.identifierChanged = action.payload;
    },

    resetState: () => initialState,

    setCurrentRouteKey: (state, action: PayloadAction<string>) => {
      state.currentRouteKey = action.payload;
    },

    setRelType: (state, action: PayloadAction<RelType>) => {
      state.relType = action.payload;
    },

    setViewMode: (state, action: PayloadAction<ViewMode>) => {
      state.viewMode = action.payload;
    },

    setEdgeLabelVisible: (state, action: PayloadAction<boolean>) => {
      state.edgeLabelVisible = action.payload;
    },

    setHoveredEdgeId: (state, action: PayloadAction<string | null>) => {
      state.hoveredEdgeId = action.payload;
    },

    setSelectionMode: (state, action: PayloadAction<SelectionMode>) => {
      state.selectionMode = action.payload;
    },

    setAutoAdjustEdges: (state, action: PayloadAction<boolean>) => {
      state.autoAdjustEdges = action.payload;
      saveToggle('autoAdjustEdges', action.payload);
      // modelConfig에도 반영 (클라우드 저장 시 포함되도록)
      if (state.modelInfo?.key) {
        state.modelInfo.modelConfig = {
          ...state.modelInfo.modelConfig,
          autoAdjustEdges: action.payload,
        };
      }
    },

    setAppInitialized: (state, action: PayloadAction<boolean>) => {
      state.appInitialized = action.payload;
    },

    setIsUndo: (state, action: PayloadAction<boolean>) => {
      state.isUndo = action.payload;
    },

    setIsRedo: (state, action: PayloadAction<boolean>) => {
      state.isRedo = action.payload;
    },

    setFocusInfo: (state, action: PayloadAction<FocusInfo>) => {
      state.focusInfo = action.payload;
    },

    setModelInfo: (state, action: PayloadAction<ModelInfo>) => {
      // 🔧 RF 내부 필드 sanitize — stale `internals` 가 있는 경우만 nodes 를 새 배열로 만든다 (성능 최적화).
      // payload 자체는 항상 새 객체로 만들어 후속 코드의 payload.key = ... mutation 이 frozen 에러를
      // 발생시키지 않도록 한다.
      const original = action.payload;
      const needsSanitize = Array.isArray(original.nodes) && original.nodes.length > 0
        && original.nodes.some((n) => n && 'internals' in n);
      const payload: ModelInfo = needsSanitize
        ? {
            ...original,
            nodes: (original.nodes as unknown[]).map((n) => {
              if (n && typeof n === 'object' && 'internals' in n) {
                const { internals: _internals, ...rest } = n as Record<string, unknown>;
                return rest;
              }
              return n;
            }) as ModelInfo['nodes'],
          }
        : { ...original };

      const originalKey = payload.key && String(payload.key).trim() !== '' ? payload.key : null;

      if (!originalKey) {
        if (state.modelInfo.key && String(state.modelInfo.key).trim() !== '') {
          payload.key = state.modelInfo.key;
        } else {
          payload.key = uid(10);
          // 호출 경로 추적 — 어떤 핸들러가 key 없이 setModelInfo를 호출했는지 콜스택으로 식별
        }
      }
      // 🛡️ Invariant: edges 의 source/target 이 nodes 에 존재해야 함.
      // 모델 전환 race 등으로 다른 모델의 edges 가 들어오는 cross-contamination 을 reducer 레벨에서 차단.
      if (Array.isArray(payload.edges) && Array.isArray(payload.nodes)) {
        const nodeIds = new Set((payload.nodes as Array<{ id?: string }>).map((n) => n?.id));
        const orphans = (payload.edges as Array<{ id?: string; source?: string; target?: string }>).filter(
          (e) => !nodeIds.has(e.source as string) || !nodeIds.has(e.target as string)
        );
        if (orphans.length > 0) {
          console.warn(
            `[setModelInfo] cross-model 오염 차단 — key=${payload.key} 의 edge ${orphans.length}/${payload.edges.length} 개가 source/target 노드 부재. 자동 제거.`,
            {
              modelName: payload.modelName,
              sample: orphans.slice(0, 3).map((e) => ({ id: e.id, source: e.source, target: e.target })),
              stack: new Error().stack?.split('\n').slice(2, 8).join('\n'),
            }
          );
          payload.edges = (payload.edges as Array<{ source?: string; target?: string }>).filter(
            (e) => nodeIds.has(e.source as string) && nodeIds.has(e.target as string)
          ) as typeof payload.edges;
        }
      }

      state.modelInfo = payload;
      // Phase 1: activeModelKey 도 함께 갱신 — 셀렉터(selectActiveModel)가 동일 모델을 가리키도록 보장
      state.activeModelKey = payload.key || null;

      // multiModelData 자동 동기화 (atomic): 활성 모델 변경 시 multiModelData[key]도 함께 갱신
      // 전체 교체로 stale 필드 제거 보장 (shallow merge 금지)
      // 🛡️ inner array deep-isolation: shallow spread 만으로는 nodes/edges/attrs 가
      // multiModelData entry 간 reference 공유될 수 있음. 새 배열로 감싸 단절.
      if (payload.key) {
        const models = state.multiModelData as Record<string, ModelInfo>;
        models[payload.key] = {
          ...payload,
          ...(Array.isArray(payload.nodes) ? { nodes: [...payload.nodes] } : {}),
          ...(Array.isArray(payload.edges) ? { edges: [...payload.edges] } : {}),
          ...(Array.isArray(payload.attrs) ? { attrs: [...payload.attrs] } : {}),
        };

        // 🔧 복제 방지: payload가 key 없이 들어와 새 key로 동기화된 경우,
        // 동일 모델이 mdlrmdlId 등 다른 키 아래에 이미 존재할 수 있음 → 정리.
        // 식별 기준: 같은 mdlrmdlId를 갖는 다른 entry. (없으면 정리 생략)
        const mdlId = payload.mdlrmdlId && String(payload.mdlrmdlId).trim() !== ''
          ? payload.mdlrmdlId : null;
        if (mdlId && !originalKey) {
          for (const k of Object.keys(models)) {
            if (k !== payload.key && models[k]?.mdlrmdlId === mdlId) {
              delete models[k];
            }
          }
        }
      }
    },

    /**
     * 활성 모델 key 만 변경 — multiModelData 에서 해당 모델을 읽어 modelInfo 에 복사.
     * Phase 1: setModelInfo 보다 가벼운 모델 전환용. 호출자는 multiModelData 에 모델이 이미 있다고 가정.
     */
    setActiveModelKey: (state, action: PayloadAction<string | null>) => {
      const key = action.payload;
      state.activeModelKey = key;
      if (key) {
        const models = state.multiModelData as Record<string, ModelInfo>;
        if (models[key]) {
          state.modelInfo = { ...models[key] };
        }
      }
    },

    setModelChanged: (state, action: PayloadAction<ModelChangedPayload>) => {
      const { modelKey, changed } = action.payload;

      if (state.modelInfo.key === modelKey) {
        state.modelInfo.changed = changed;
      }

      const models = state.multiModelData as Record<string, ModelInfo>;
      if (models[modelKey]) {
        models[modelKey].changed = changed;
      }
    },

    markCurrentModelChanged: (state) => {
      if (state.modelInfo.key) {
        state.modelInfo.changed = true;

        const models = state.multiModelData as Record<string, ModelInfo>;
        if (models[state.modelInfo.key]) {
          models[state.modelInfo.key].changed = true;
        }
      }
    },

    setEntityInfo: (state, action: PayloadAction<EntityInfo>) => {
      state.entityInfo = action.payload;
    },

    updateEntityName: (state, action: PayloadAction<string>) => {
      state.entityInfo.data.entityName = action.payload;
    },

    updatePhysicalName: (state, action: PayloadAction<string>) => {
      state.entityInfo.data.physicalName = action.payload;
    },

    setModelInfoEdges: (state, action: PayloadAction<EdgeUpdatePayload>) => {
      const { id, ...updates } = action.payload;
      state.modelInfo.edges = state.modelInfo.edges.map((edge) =>
        edge.id === id ? { ...edge, ...updates } : edge
      );
      // Phase 2 미러링 — 활성 모델의 multiModelData 항목도 함께 갱신 (속성 reducer 패턴 동일)
      if (state.modelInfo.key) {
        const models = state.multiModelData as Record<string, ModelInfo>;
        if (models[state.modelInfo.key]) {
          models[state.modelInfo.key].edges = state.modelInfo.edges;
        }
      }
    },

    /**
     * React Flow 의 NodeChange[] 를 Redux modelInfo.nodes 에 커밋 (Phase 3).
     * @xyflow/react 의 applyNodeChanges 유틸을 사용하여 모든 change 타입(position/dimensions/select/remove/add) 처리.
     * multiModelData[activeKey] 에도 즉시 미러링 — 모델 전환 시 rfInstance.toObject() 의존을 제거하기 위함.
     * (이름이 commit* 인 이유: @xyflow/react 의 applyNodeChanges 함수와 호출처에서 이름 충돌 회피)
     */
    commitNodeChanges: (state, action: PayloadAction<NodeChange[]>) => {
      const currentNodes = (state.modelInfo.nodes ?? []) as Parameters<typeof rfApplyNodeChanges>[1];
      const next = rfApplyNodeChanges(action.payload, currentNodes);
      state.modelInfo.nodes = next as typeof state.modelInfo.nodes;
      if (state.modelInfo.key) {
        const models = state.multiModelData as Record<string, ModelInfo>;
        if (models[state.modelInfo.key]) {
          models[state.modelInfo.key].nodes = state.modelInfo.nodes;
        }
      }
    },

    /**
     * React Flow 의 EdgeChange[] 를 Redux modelInfo.edges 에 커밋 (Phase 3).
     */
    commitEdgeChanges: (state, action: PayloadAction<EdgeChange[]>) => {
      const currentEdges = (state.modelInfo.edges ?? []) as Parameters<typeof rfApplyEdgeChanges>[1];
      const next = rfApplyEdgeChanges(action.payload, currentEdges);
      state.modelInfo.edges = next as typeof state.modelInfo.edges;
      if (state.modelInfo.key) {
        const models = state.multiModelData as Record<string, ModelInfo>;
        if (models[state.modelInfo.key]) {
          models[state.modelInfo.key].edges = state.modelInfo.edges;
        }
      }
    },

    setMultiModelData: (state, action: PayloadAction<Record<string, ModelInfo>>) => {
      // 🔧 RF 내부 필드(internals) sanitize — 이전 fix 시도가 stale internals 로 persist 한 데이터 회복.
      // RF v12 의 nodeToBox 가 `'internals' in element` 로 internalNode 판정하여 crash 회피.
      const sanitized = { ...action.payload };
      for (const k of Object.keys(sanitized)) {
        const m = sanitized[k];
        if (m && Array.isArray(m.nodes)) {
          sanitized[k] = {
            ...m,
            nodes: m.nodes.map((n) => {
              if (n && 'internals' in n) {
                const { internals: _internals, ...rest } = n as Record<string, unknown>;
                return rest as typeof n;
              }
              return n;
            }),
          };
        }
      }
      state.multiModelData = sanitized;
    },

    addMultiModelData: (state, action: PayloadAction<ModelInfo | ModelInfo[]>) => {

      const models = state.multiModelData as Record<string, ModelInfo>;

      const addModel = (model: ModelInfo) => {
        const hasValidId = model.mdlrmdlId && model.mdlrmdlId.trim() !== '';
        const hasValidKey = model.key && String(model.key).trim() !== '';

        if (!hasValidId && !hasValidKey) {
          if (model.nodes?.length === 0 && model.edges?.length === 0) {
          } else {
            console.warn('Both mdlrmdlId and key are missing for model:', model);
          }
          return;
        }

        // 🔧 RF 내부 필드 sanitize (setModelInfo 와 동일 사유 — stale internals 회복).
        // Immer 가 action.payload(model) 을 frozen 으로 만들기 때문에 직접 mutate 불가.
        // → 새 배열로 만들고 model.nodes 자리만 바꾸지 말고, 아래 merge 단계에서 sanitized 본을 사용.
        const sanitizedNodes = Array.isArray(model.nodes)
          ? model.nodes.map((n) => {
              if (n && 'internals' in n) {
                const { internals: _internals, ...rest } = n as Record<string, unknown>;
                return rest as typeof n;
              }
              return n;
            })
          : model.nodes;

        const key = model.key || model.mdlrmdlId;
        if (key) {
          // 🛡️ Invariant: 모든 edge 는 source/target 이 같은 entry 의 nodes 에 존재해야 함.
          // cross-model 오염(예: race condition 으로 다른 모델의 edges 가 들어옴)을
          // 여기서 차단. orphan 제거 후 경고 + 콜스택 로깅으로 발생 경로 추적 가능.
          const finalNodes = sanitizedNodes !== undefined
            ? sanitizedNodes
            : (models[key]?.nodes as ModelInfo['nodes'] | undefined);
          let finalEdges = model.edges;
          if (Array.isArray(model.edges) && Array.isArray(finalNodes)) {
            const nodeIds = new Set(finalNodes.map((n) => (n as { id?: string })?.id));
            const orphans = model.edges.filter((e) => {
              const ed = e as { source?: string; target?: string };
              return !nodeIds.has(ed.source as string) || !nodeIds.has(ed.target as string);
            });
            if (orphans.length > 0) {
              console.warn(
                `[addMultiModelData] cross-model 오염 차단 — key=${key} 의 edge ${orphans.length}/${model.edges.length} 개가 source/target 노드 부재. 자동 제거.`,
                {
                  sample: orphans.slice(0, 3).map((e) => ({
                    id: (e as { id?: string }).id,
                    source: (e as { source?: string }).source,
                    target: (e as { target?: string }).target,
                  })),
                  stack: new Error().stack?.split('\n').slice(2, 8).join('\n'),
                }
              );
              finalEdges = model.edges.filter((e) => {
                const ed = e as { source?: string; target?: string };
                return nodeIds.has(ed.source as string) && nodeIds.has(ed.target as string);
              });
            }
          }

          // 🛡️ inner array deep-isolation: shallow spread 만으로는 nodes/edges/attrs 가
          // reference 공유될 수 있음. 새 배열로 감싸 cross-entry 참조 단절.
          const mergedModel: ModelInfo = {
            ...models[key],
            ...model,
            ...(sanitizedNodes !== undefined ? { nodes: [...(sanitizedNodes as unknown[])] as ModelInfo['nodes'] } : {}),
            ...(finalEdges !== undefined ? { edges: [...(finalEdges as unknown[])] as ModelInfo['edges'] } : {}),
            ...(Array.isArray(model.attrs) ? { attrs: [...model.attrs] as ModelInfo['attrs'] } : {}),
          };
          models[key] = mergedModel;

          // Phase 2 미러링 — 활성 모델이 갱신되면 state.modelInfo 의 nodes/edges/viewport 도 동일하게 갱신.
          // multiModelData 와 다른 reference 가 되도록 새 배열로 미러링 (isolation 유지).
          if (state.modelInfo.key && state.modelInfo.key === key) {
            if (sanitizedNodes !== undefined) state.modelInfo.nodes = [...(sanitizedNodes as unknown[])] as ModelInfo['nodes'];
            if (finalEdges !== undefined) state.modelInfo.edges = [...(finalEdges as unknown[])] as ModelInfo['edges'];
            if (model.viewport !== undefined) state.modelInfo.viewport = model.viewport;
          }
        }
      };

      if (Array.isArray(action.payload)) {
        action.payload.forEach(addModel);
      } else {
        addModel(action.payload);
      }
    },

    renameMultiModelData: (state, action: PayloadAction<RenameModelPayload>) => {
      const { modelKey, newModelName } = action.payload;

      if (!modelKey) {
        throw new Error('Model key is missing');
      }

      const models = state.multiModelData as Record<string, ModelInfo>;
      if (!models[modelKey]) {
        throw new Error(`Model with key ${modelKey} not found`);
      }

      models[modelKey].modelName = newModelName;
    },

    renameProjectInMultiModelData: (state, action: PayloadAction<RenameProjectPayload>) => {
      const { oldProjectNm, newProjectNm } = action.payload;

      const models = state.multiModelData as Record<string, ModelInfo>;
      Object.values(models).forEach((model) => {
        if (model.projectNm === oldProjectNm) {
          model.projectNm = newProjectNm;
          model.changed = true;
        }
      });

      if (state.modelInfo.projectNm === oldProjectNm) {
        state.modelInfo.projectNm = newProjectNm;
        state.modelInfo.changed = true;
      }
    },

    deleteMultiModelData: (state, action: PayloadAction<string>) => {
      const modelKey = action.payload;

      if (!modelKey) {
        throw new Error('Model key is missing');
      }

      const models = state.multiModelData as Record<string, ModelInfo>;
      if (!models[modelKey]) {
        throw new Error(`Model with key ${modelKey} not found`);
      }

      delete models[modelKey];
    },

    setItemMenu: (state, action: PayloadAction<unknown[]>) => {
      state.itemMenu = action.payload;
    },

    setDiagramMode: (state, action: PayloadAction<DiagramMode>) => {
      state.diagramMode = action.payload;
      saveToggle('diagramMode', action.payload);
      // modelConfig에도 반영 (클라우드 저장 시 포함되도록)
      if (state.modelInfo?.key) {
        state.modelInfo.modelConfig = {
          ...state.modelInfo.modelConfig,
          diagramMode: action.payload,
        };
      }
    },

    setUserLoggingMode: (state, action: PayloadAction<string>) => {
      state.userLoggingMode = action.payload;
    },

    addAttrs: (state, action: PayloadAction<unknown | unknown[]>) => {
      if (!state.modelInfo) {
        state.modelInfo = { attrs: [] } as ModelInfo;
      }
      state.modelInfo.attrs = state.modelInfo.attrs || [];
      const newAttrs = Array.isArray(action.payload) ? action.payload : [action.payload];
      state.modelInfo.attrs = [...state.modelInfo.attrs, ...newAttrs];
      // multiModelData 동기화 (모델 전환 시 데이터 손실 방지)
      if (state.modelInfo.key) {
        const models = state.multiModelData as Record<string, ModelInfo>;
        if (models[state.modelInfo.key]) {
          models[state.modelInfo.key].attrs = state.modelInfo.attrs;
        }
      }
    },

    replaceAttrs: (state, action: PayloadAction<unknown[]>) => {
      state.modelInfo.attrs = action.payload as AttributeGroup[];
      // multiModelData 동기화
      if (state.modelInfo.key) {
        const models = state.multiModelData as Record<string, ModelInfo>;
        if (models[state.modelInfo.key]) {
          models[state.modelInfo.key].attrs = state.modelInfo.attrs;
        }
      }
    },

    /**
     * 특정 모델의 attrs 업데이트 (멀티 모델 일괄 작업용)
     * - 현재 모델이면 state.modelInfo.attrs 업데이트
     * - multiModelData에 있으면 해당 모델의 attrs도 업데이트
     */
    updateModelAttrsById: (state, action: PayloadAction<UpdateModelAttrsByIdPayload>) => {
      const { modelKey, attrs } = action.payload;
      const isCurrentModel = state.modelInfo.key === modelKey;
      const models = state.multiModelData as Record<string, ModelInfo>;
      const model = models[modelKey];

      // 현재 모델이면 state.modelInfo.attrs 업데이트
      if (isCurrentModel) {
        state.modelInfo.attrs = attrs as AttributeGroup[];
        state.modelInfo.changed = true;
      }

      // multiModelData에 있으면 해당 모델의 attrs도 업데이트
      if (model) {
        model.attrs = attrs as AttributeGroup[];
        model.changed = true;
      }
    },

    addOrReplaceAttrs: (state, action: PayloadAction<unknown | unknown[]>) => {
      if (!state.modelInfo) {
        state.modelInfo = { attrs: [] } as ModelInfo;
      }
      state.modelInfo.attrs = state.modelInfo.attrs || [];

      const newAttrs = Array.isArray(action.payload) ? action.payload : [action.payload];
      const filteredExistingAttrs = state.modelInfo.attrs.filter(
        (existingAttr) =>
          !newAttrs.some(
            (newAttr) =>
              (newAttr as { entityId: string }).entityId ===
              (existingAttr as { entityId: string }).entityId
          )
      );
      state.modelInfo.attrs = [...filteredExistingAttrs, ...newAttrs] as AttributeGroup[];
      // multiModelData 동기화
      if (state.modelInfo.key) {
        const models = state.multiModelData as Record<string, ModelInfo>;
        if (models[state.modelInfo.key]) {
          models[state.modelInfo.key].attrs = state.modelInfo.attrs;
        }
      }
    },

    removeEdgeItem: (state, action: PayloadAction<EdgeItemPayload>) => {
      state.modelInfo.attrs = state.modelInfo.attrs.map((attr) => {
        const attrData = attr as { entityId: string; items: Array<{ edgeId?: string }> };
        if (attrData.entityId === action.payload.entityId) {
          return {
            ...attr,
            items: attrData.items.filter((item) => item.edgeId !== action.payload.edgeId),
          } as AttributeGroup;
        }
        return attr;
      });
      // multiModelData 동기화
      if (state.modelInfo.key) {
        const models = state.multiModelData as Record<string, ModelInfo>;
        if (models[state.modelInfo.key]) {
          models[state.modelInfo.key].attrs = state.modelInfo.attrs;
        }
      }
    },

    mergeItems: (state, action: PayloadAction<MergeItemsPayload>) => {
      const { entityId, items } = action.payload;
      state.modelInfo.attrs = state.modelInfo.attrs.map((attr) => {
        const attrData = attr as { entityId: string; items: Array<{ id?: string }> };
        if (attrData.entityId === entityId) {
          const existingIds = new Set(
            attrData.items.filter((item) => item.id !== undefined).map((item) => item.id)
          );
          const mergedItems = [
            ...attrData.items,
            ...(items as Array<{ id?: string }>).filter(
              (item) => item && item.id !== undefined && !existingIds.has(item.id)
            ),
          ];
          return { ...attr, items: mergedItems } as AttributeGroup;
        }
        return attr;
      });
      // multiModelData 동기화
      if (state.modelInfo.key) {
        const models = state.multiModelData as Record<string, ModelInfo>;
        if (models[state.modelInfo.key]) {
          models[state.modelInfo.key].attrs = state.modelInfo.attrs;
        }
      }
    },

    replaceAttrItems: (state, action: PayloadAction<ReplaceAttrItemsPayload>) => {
      state.modelInfo.attrs = state.modelInfo.attrs.map((attr) => {
        const attrData = attr as { entityId: string };
        if (attrData.entityId === action.payload.entityId) {
          return { ...attr, items: action.payload.items } as AttributeGroup;
        }
        return attr;
      });
      // multiModelData 동기화 (모델 전환 시 데이터 손실 방지)
      if (state.modelInfo.key) {
        const models = state.multiModelData as Record<string, ModelInfo>;
        if (models[state.modelInfo.key]) {
          models[state.modelInfo.key].attrs = state.modelInfo.attrs;
        }
      }
    },

    applyQualityResults: (state, action: PayloadAction<QualityResultsPayload>) => {
      const { modelKey, entityId, qualityByAttr } = action.payload;
      const isCurrentModel = state.modelInfo.key === modelKey;
      const models = state.multiModelData as Record<string, ModelInfo>;
      const model = models[modelKey];

      if (!isCurrentModel && !model) {
        return;
      }

      const applyQualityToAttrs = (attrs: AttributeGroup[]) => {
        return attrs.map((attr) => {
          const attrData = attr as {
            entityId: string;
            items: Array<{ physicalName?: string; qualityResult?: unknown }>;
          };
          if (entityId && attrData.entityId !== entityId) {
            return attr;
          }

          let attributesWithQuality = 0;
          let totalEmptyRatio = 0;
          let totalNullCount = 0;
          let totalBlankCount = 0;
          let totalZeroCount = 0;
          let totalRecordCount = 0;
          let latestInspectedAt: string | null = null;
          const columnTypes: Record<string, number> = { string: 0, number: 0, date: 0 };

          const updatedItems = attrData.items.map((item) => {
            const qualityResult = qualityByAttr[item.physicalName || ''];
            if (qualityResult) {
              attributesWithQuality++;
              totalEmptyRatio += (qualityResult.emptyRatio as number) || 0;
              totalNullCount += (qualityResult.nullCount as number) || 0;
              totalBlankCount += (qualityResult.blankCount as number) || 0;
              totalZeroCount += (qualityResult.zeroCount as number) || 0;
              totalRecordCount += (qualityResult.totalCount as number) || 0;

              if (qualityResult.columnType) {
                columnTypes[qualityResult.columnType as string] =
                  (columnTypes[qualityResult.columnType as string] || 0) + 1;
              }

              if (qualityResult.inspectedAt) {
                if (!latestInspectedAt || (qualityResult.inspectedAt as string) > latestInspectedAt) {
                  latestInspectedAt = qualityResult.inspectedAt as string;
                }
              }

              const { inspectedAt, ...qualityWithoutDate } = qualityResult;
              return { ...item, qualityResult: qualityWithoutDate };
            }
            return item;
          });

          const totalAttributes = updatedItems.length;

          const qualitySummary =
            attributesWithQuality > 0
              ? {
                  totalAttributes,
                  attributesWithQuality,
                  avgEmptyRatio: totalEmptyRatio / attributesWithQuality,
                  totalNullCount,
                  totalBlankCount,
                  totalZeroCount,
                  totalRecordCount,
                  columnTypes,
                  overallEmptyRatio:
                    totalRecordCount > 0
                      ? (totalNullCount + totalBlankCount + totalZeroCount) / totalRecordCount
                      : 0,
                }
              : null;

          return {
            ...attr,
            items: updatedItems,
            ...(qualitySummary && { qualitySummary }),
            ...(latestInspectedAt && { inspectedAt: latestInspectedAt }),
          } as unknown as AttributeGroup;
        });
      };

      if (isCurrentModel) {
        state.modelInfo.attrs = applyQualityToAttrs(state.modelInfo.attrs);
        state.modelInfo.changed = true;
      }

      if (model?.attrs) {
        model.attrs = applyQualityToAttrs(model.attrs);
        model.changed = true;
      }
    },

    applyQualityResultsToEntity: (state, action: PayloadAction<EntityQualityPayload>) => {
      const { entityId, qualityByAttr } = action.payload;

      state.modelInfo.attrs = state.modelInfo.attrs.map((attr) => {
        const attrData = attr as {
          entityId: string;
          items: Array<{ physicalName?: string; qualityResult?: unknown }>;
        };
        if (attrData.entityId === entityId) {
          const updatedItems = attrData.items.map((item) => {
            const qualityResult = qualityByAttr[item.physicalName || ''];
            if (qualityResult) {
              return { ...item, qualityResult };
            }
            return item;
          });
          return { ...attr, items: updatedItems } as AttributeGroup;
        }
        return attr;
      });

      state.modelInfo.changed = true;
    },

    clearAllQualityResults: (state) => {
      state.modelInfo.attrs = state.modelInfo.attrs.map((attr) => {
        const attrData = attr as { items: Array<{ qualityResult?: unknown }> };
        const updatedItems = attrData.items.map((item) => {
          if (item.qualityResult) {
            const { qualityResult, ...rest } = item;
            return rest;
          }
          return item;
        });
        return { ...attr, items: updatedItems } as AttributeGroup;
      });

      state.modelInfo.changed = true;
    },

    replaceMultipleAttrItems: (state, action: PayloadAction<MultipleAttrItemsPayload>) => {
      const updates = action.payload.updates;
      if (!Array.isArray(updates)) {
        console.error('Invalid updates payload:', updates);
        return;
      }
      updates.forEach((update) => {
        state.modelInfo.attrs = state.modelInfo.attrs.map((attr) => {
          const attrData = attr as { entityId: string };
          if (attrData.entityId === update.entityId) {
            return { ...attr, items: update.items } as AttributeGroup;
          }
          return attr;
        });
      });
    },

    deselectItems: (state, action: PayloadAction<DeselectItemsPayload>) => {
      const { entityId } = action.payload;

      if (!Array.isArray(state.modelInfo.attrs)) return;

      state.modelInfo.attrs = state.modelInfo.attrs.map((attr) => {
        const attrData = attr as { entityId: string; items: Array<{ selected?: boolean }> };
        if (attrData.entityId !== entityId && Array.isArray(attrData.items)) {
          const updatedItems = attrData.items.map((item) => {
            if (item.selected) {
              return { ...item, selected: false };
            }
            return item;
          });
          return { ...attr, items: updatedItems } as AttributeGroup;
        }
        return attr;
      });
    },

    toggleDetailVisibility: (state) => {
      state.isDetailVisible = !state.isDetailVisible;
    },

    setIsDetailVisible: (state, action: PayloadAction<boolean>) => {
      state.isDetailVisible = action.payload;
    },

    // Dictionary actions
    setDictionaryWordsLoading: (state) => {
      state.dictionary.loading = true;
      state.dictionary.error = null;
    },

    setDictionaryWordsSuccess: (state, action: PayloadAction<unknown[]>) => {
      state.dictionary.words = action.payload;
      state.dictionary.loading = false;
    },

    setDictionaryWordsFailure: (state, action: PayloadAction<string>) => {
      state.dictionary.loading = false;
      state.dictionary.error = action.payload;
    },

    setDictionaryTermsLoading: (state) => {
      state.dictionary.loading = true;
      state.dictionary.error = null;
    },

    setDictionaryTermsSuccess: (state, action: PayloadAction<unknown[]>) => {
      state.dictionary.terms = action.payload;
      state.dictionary.loading = false;
    },

    setDictionaryTermsFailure: (state, action: PayloadAction<string>) => {
      state.dictionary.loading = false;
      state.dictionary.error = action.payload;
    },

    setDictionaryDomainGroupLoading: (state) => {
      state.dictionary.loading = true;
      state.dictionary.error = null;
    },

    setDictionaryDomainGroupSuccess: (state, action: PayloadAction<unknown[]>) => {
      state.dictionary.domainGroup = action.payload;
      state.dictionary.loading = false;
    },

    setDictionaryDomainGroupFailure: (state, action: PayloadAction<string>) => {
      state.dictionary.loading = false;
      state.dictionary.error = action.payload;
    },

    setDictionaryInfotypeLoading: (state) => {
      state.dictionary.loading = true;
      state.dictionary.error = null;
    },

    setDictionaryInfotypeSuccess: (state, action: PayloadAction<unknown[]>) => {
      state.dictionary.infotype = action.payload;
      state.dictionary.loading = false;
    },

    setDictionaryInfotypeFailure: (state, action: PayloadAction<string>) => {
      state.dictionary.loading = false;
      state.dictionary.error = action.payload;
    },

    updateEntityPhysicalNames: (state, action: PayloadAction<EntityPhysicalNameUpdate[]>) => {
      state.modelInfo.nodes = state.modelInfo.nodes.map((node) => {
        const update = action.payload.find((u) => u.entityId === node.id);
        if (update) {
          return {
            ...node,
            data: { ...node.data, physicalName: update.newPhysicalName },
            entityType: '',
          };
        }
        return node;
      });
      // Phase 2 미러링 — 활성 모델의 multiModelData 항목도 함께 갱신
      if (state.modelInfo.key) {
        const models = state.multiModelData as Record<string, ModelInfo>;
        if (models[state.modelInfo.key]) {
          models[state.modelInfo.key].nodes = state.modelInfo.nodes;
        }
      }
    },

    setModelConfig: (state, action: PayloadAction<SetModelConfigPayload>) => {
      const { modelKey, mdlrmdlId, modelConfig } = action.payload;

      const models = state.multiModelData as Record<string, ModelInfo>;
      let modelToUpdate: ModelInfo | undefined;
      let foundKey: string | undefined;

      if (mdlrmdlId) {
        const entry = Object.entries(models).find(([_, m]) => m.mdlrmdlId === mdlrmdlId);
        if (entry) {
          [foundKey, modelToUpdate] = entry;
        }
      }

      if (!modelToUpdate && modelKey) {
        modelToUpdate = models[modelKey];
        foundKey = modelKey;
      }

      if (!modelToUpdate || !foundKey) {
        console.error(`모델을 찾을 수 없습니다. modelKey: ${modelKey}, mdlrmdlId: ${mdlrmdlId}`);
        return;
      }

      const currentDate = new Date().toISOString();

      models[foundKey].modelConfig = {
        ...modelConfig,
        lastModified: currentDate,
      };

      if (state.modelInfo.key === foundKey) {
        state.modelInfo.modelConfig = {
          ...modelConfig,
          lastModified: currentDate,
        };
      }
    },

    updateModelConfigTab: (state, action: PayloadAction<ModelConfigTabPayload>) => {
      const { modelId, tabIndex, newTabData } = action.payload;
      const configs = state.modelConfigs as Array<{
        tabs?: Array<Record<string, unknown>>;
      }>;
      if (configs[Number(modelId)]?.tabs?.[tabIndex]) {
        configs[Number(modelId)].tabs![tabIndex] = {
          ...configs[Number(modelId)].tabs![tabIndex],
          ...newTabData,
        };
      }
    },

    updateModelConfigSection: (state, action: PayloadAction<ModelConfigSectionPayload>) => {
      const { modelId, tabIndex, sectionIndex, newSectionData } = action.payload;
      const configs = state.modelConfigs as Array<{
        tabs?: Array<{ sections?: Array<Record<string, unknown>> }>;
      }>;
      if (configs[Number(modelId)]?.tabs?.[tabIndex]?.sections?.[sectionIndex]) {
        configs[Number(modelId)].tabs![tabIndex].sections![sectionIndex] = {
          ...configs[Number(modelId)].tabs![tabIndex].sections![sectionIndex],
          ...newSectionData,
        };
      }
    },

    removeModelConfig: (state, action: PayloadAction<string>) => {
      const modelId = action.payload;
      const configs = state.modelConfigs as unknown as Record<string, unknown>;
      delete configs[modelId];
    },

    // Analysis results
    setCoreEntityAnalysisResult: (state, action: PayloadAction<AnalysisResultPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.analysisResults) {
        state.analysisResults = {};
      }
      if (!state.analysisResults[modelKey]) {
        state.analysisResults[modelKey] = {};
      }
      state.analysisResults[modelKey].coreEntityAnalysis = {
        ...result,
        timestamp: new Date().toISOString(),
      };
    },

    setRelationshipInferenceResult: (state, action: PayloadAction<AnalysisResultPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.analysisResults) {
        state.analysisResults = {};
      }
      if (!state.analysisResults[modelKey]) {
        state.analysisResults[modelKey] = {};
      }
      state.analysisResults[modelKey].relationshipInference = {
        ...result,
        timestamp: new Date().toISOString(),
      };
    },

    setEntityClassificationResult: (state, action: PayloadAction<AnalysisResultPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.analysisResults) {
        state.analysisResults = {};
      }
      if (!state.analysisResults[modelKey]) {
        state.analysisResults[modelKey] = {};
      }
      state.analysisResults[modelKey].entityClassification = {
        ...result,
        timestamp: new Date().toISOString(),
      };
    },

    clearAnalysisResults: (state, action: PayloadAction<string | undefined>) => {
      const modelKey = action.payload;
      if (!state.analysisResults) {
        state.analysisResults = {};
        return;
      }
      if (modelKey) {
        delete state.analysisResults[modelKey];
      } else {
        state.analysisResults = {};
      }
    },

    setCandidateKeyResult: (state, action: PayloadAction<AnalysisResultPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.analysisResults) {
        state.analysisResults = {};
      }
      if (!state.analysisResults[modelKey]) {
        state.analysisResults[modelKey] = {};
      }
      state.analysisResults[modelKey].candidateKeyAnalysis = {
        ...result,
        timestamp: new Date().toISOString(),
      };
    },

    setEntitySummaryResult: (state, action: PayloadAction<AnalysisResultPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.analysisResults) {
        state.analysisResults = {};
      }
      if (!state.analysisResults[modelKey]) {
        state.analysisResults[modelKey] = {};
      }

      const existingSummary = state.analysisResults[modelKey].entitySummary as
        | { results?: unknown[] }
        | undefined;
      const existingResults = existingSummary?.results || [];
      const newResults = (result as { results?: unknown[] }).results || [];

      let mergedResults = [...existingResults];
      newResults.forEach((newResult) => {
        const nr = newResult as { entityId?: string };
        const existingIndex = mergedResults.findIndex(
          (r) => (r as { entityId?: string }).entityId === nr.entityId
        );
        if (existingIndex >= 0) {
          mergedResults[existingIndex] = newResult;
        } else {
          mergedResults.push(newResult);
        }
      });

      state.analysisResults[modelKey].entitySummary = {
        ...result,
        results: mergedResults,
        timestamp: new Date().toISOString(),
      };
    },

    // Legacy naming rule result (stored in analysisResults)
    setNamingRuleResult: (state, action: PayloadAction<AnalysisResultPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.analysisResults) {
        state.analysisResults = {};
      }
      if (!state.analysisResults[modelKey]) {
        state.analysisResults[modelKey] = {};
      }
      state.analysisResults[modelKey].namingRule = {
        ...result,
        timestamp: new Date().toISOString(),
      };
    },

    setWordCandidateResult: (state, action: PayloadAction<AnalysisResultPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.analysisResults) {
        state.analysisResults = {};
      }
      if (!state.analysisResults[modelKey]) {
        state.analysisResults[modelKey] = {};
      }
      state.analysisResults[modelKey].wordCandidate = {
        ...result,
        timestamp: new Date().toISOString(),
      };
    },

    setTermCandidateResult: (state, action: PayloadAction<AnalysisResultPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.analysisResults) {
        state.analysisResults = {};
      }
      if (!state.analysisResults[modelKey]) {
        state.analysisResults[modelKey] = {};
      }
      state.analysisResults[modelKey].termCandidate = {
        ...result,
        timestamp: new Date().toISOString(),
      };
    },

    setDomainCandidateResult: (state, action: PayloadAction<AnalysisResultPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.analysisResults) {
        state.analysisResults = {};
      }
      if (!state.analysisResults[modelKey]) {
        state.analysisResults[modelKey] = {};
      }
      state.analysisResults[modelKey].domainCandidate = {
        ...result,
        timestamp: new Date().toISOString(),
      };
    },

    setProjectTermCandidateResult: (state, action: PayloadAction<ProjectTermPayload>) => {
      const { projectKey, projectResult, modelResults } = action.payload;
      if (!state.analysisResults) {
        state.analysisResults = {};
      }
      const projectResultKey = `project_${projectKey || 'default'}`;
      state.analysisResults[projectResultKey] = {
        ...state.analysisResults[projectResultKey],
        projectTermCandidateAnalysis: {
          ...projectResult,
          timestamp: new Date().toISOString(),
        },
      };
      if (modelResults && Array.isArray(modelResults)) {
        modelResults.forEach((modelResult) => {
          const { modelKey, ...rest } = modelResult;
          if (!state.analysisResults[modelKey]) {
            state.analysisResults[modelKey] = {};
          }
          state.analysisResults[modelKey].termCandidate = {
            ...rest,
            timestamp: new Date().toISOString(),
          };
        });
      }
    },

    clearProjectTermCandidateResult: (state, action: PayloadAction<string | undefined>) => {
      const projectKey = action.payload;
      if (!state.analysisResults) {
        return;
      }
      const projectResultKey = `project_${projectKey || 'default'}`;
      if (state.analysisResults[projectResultKey]) {
        delete state.analysisResults[projectResultKey].projectTermCandidateAnalysis;
      }
    },

    // Legacy workflow actions (moved to daExtensionSlice)
    initTermCandidateWorkflow: (state, action: PayloadAction<ModelKeyPayload>) => {
      const { modelKey } = action.payload;
      if (!state.termCandidateWorkflow) {
        state.termCandidateWorkflow = {};
      }
      state.termCandidateWorkflow[modelKey] = {
        currentStep: 1,
        step1: undefined,
        step2: undefined,
        step3: undefined,
        step4: undefined,
        step5: undefined,
        lastUpdated: new Date().toISOString(),
      };
    },

    setTermWorkflowStep1: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.termCandidateWorkflow) {
        state.termCandidateWorkflow = {};
      }
      if (!state.termCandidateWorkflow[modelKey]) {
        state.termCandidateWorkflow[modelKey] = { currentStep: 1 };
      }
      state.termCandidateWorkflow[modelKey].step1 = {
        ...result,
        timestamp: new Date().toISOString(),
      };
      state.termCandidateWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setTermWorkflowStep2: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.termCandidateWorkflow) {
        state.termCandidateWorkflow = {};
      }
      if (!state.termCandidateWorkflow[modelKey]) {
        state.termCandidateWorkflow[modelKey] = { currentStep: 1 };
      }
      state.termCandidateWorkflow[modelKey].step2 = {
        ...result,
        timestamp: new Date().toISOString(),
      };
      state.termCandidateWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setTermWorkflowStep3: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.termCandidateWorkflow) {
        state.termCandidateWorkflow = {};
      }
      if (!state.termCandidateWorkflow[modelKey]) {
        state.termCandidateWorkflow[modelKey] = { currentStep: 1 };
      }
      state.termCandidateWorkflow[modelKey].step3 = {
        ...result,
        timestamp: new Date().toISOString(),
      };
      state.termCandidateWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setTermWorkflowStep4: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.termCandidateWorkflow) {
        state.termCandidateWorkflow = {};
      }
      if (!state.termCandidateWorkflow[modelKey]) {
        state.termCandidateWorkflow[modelKey] = { currentStep: 1 };
      }
      state.termCandidateWorkflow[modelKey].step4 = {
        ...result,
        timestamp: new Date().toISOString(),
      };
      state.termCandidateWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setTermWorkflowStep5: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.termCandidateWorkflow) {
        state.termCandidateWorkflow = {};
      }
      if (!state.termCandidateWorkflow[modelKey]) {
        state.termCandidateWorkflow[modelKey] = { currentStep: 1 };
      }
      state.termCandidateWorkflow[modelKey].step5 = {
        ...result,
        timestamp: new Date().toISOString(),
      };
      state.termCandidateWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setTermWorkflowCurrentStep: (state, action: PayloadAction<SetCurrentStepPayload>) => {
      const { modelKey, step } = action.payload;
      if (!state.termCandidateWorkflow) {
        state.termCandidateWorkflow = {};
      }
      if (!state.termCandidateWorkflow[modelKey]) {
        state.termCandidateWorkflow[modelKey] = { currentStep: 0 };
      }
      state.termCandidateWorkflow[modelKey].currentStep = step;
      state.termCandidateWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    clearTermCandidateWorkflow: (state, action: PayloadAction<string | undefined>) => {
      const modelKey = action.payload;
      if (!state.termCandidateWorkflow) {
        return;
      }
      if (modelKey) {
        delete state.termCandidateWorkflow[modelKey];
      } else {
        state.termCandidateWorkflow = {};
      }
    },

    // Model refine results (legacy)
    setEntityRefineResult: (state, action: PayloadAction<LegacyRefineResult | null>) => {
      state.entityRefineResult = action.payload;
    },

    setAttributeRefineResult: (state, action: PayloadAction<LegacyRefineResult | null>) => {
      state.attributeRefineResult = action.payload;
    },

    clearEntityRefineResult: (state) => {
      state.entityRefineResult = null;
    },

    clearAttributeRefineResult: (state) => {
      state.attributeRefineResult = null;
    },

    // Entity refine workflow (legacy)
    initEntityRefineWorkflow: (state, action: PayloadAction<ModelKeyPayload>) => {
      const { modelKey } = action.payload;
      if (!state.entityRefineWorkflow) {
        state.entityRefineWorkflow = {};
      }
      state.entityRefineWorkflow[modelKey] = {
        currentStep: 0,
        step1: undefined,
        step2: undefined,
        step3: undefined,
        step4: undefined,
        llmSettings: { model: null, skipCache: false },
        lastUpdated: new Date().toISOString(),
      };
    },

    setEntityRefineStep1: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.entityRefineWorkflow) {
        state.entityRefineWorkflow = {};
      }
      if (!state.entityRefineWorkflow[modelKey]) {
        state.entityRefineWorkflow[modelKey] = { currentStep: 0 };
      }
      state.entityRefineWorkflow[modelKey].step1 = result;
      state.entityRefineWorkflow[modelKey].currentStep = Math.max(
        state.entityRefineWorkflow[modelKey].currentStep || 0,
        0
      );
      state.entityRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setEntityRefineStep2: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.entityRefineWorkflow) {
        state.entityRefineWorkflow = {};
      }
      if (!state.entityRefineWorkflow[modelKey]) {
        state.entityRefineWorkflow[modelKey] = { currentStep: 0 };
      }
      state.entityRefineWorkflow[modelKey].step2 = result;
      state.entityRefineWorkflow[modelKey].currentStep = Math.max(
        state.entityRefineWorkflow[modelKey].currentStep || 0,
        1
      );
      state.entityRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setEntityRefineStep3: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.entityRefineWorkflow) {
        state.entityRefineWorkflow = {};
      }
      if (!state.entityRefineWorkflow[modelKey]) {
        state.entityRefineWorkflow[modelKey] = { currentStep: 0 };
      }
      state.entityRefineWorkflow[modelKey].step3 = result;
      state.entityRefineWorkflow[modelKey].currentStep = Math.max(
        state.entityRefineWorkflow[modelKey].currentStep || 0,
        2
      );
      state.entityRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setEntityRefineStep4: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.entityRefineWorkflow) {
        state.entityRefineWorkflow = {};
      }
      if (!state.entityRefineWorkflow[modelKey]) {
        state.entityRefineWorkflow[modelKey] = { currentStep: 0 };
      }
      state.entityRefineWorkflow[modelKey].step4 = result;
      state.entityRefineWorkflow[modelKey].currentStep = 3;
      state.entityRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setEntityRefineCurrentStep: (state, action: PayloadAction<SetCurrentStepPayload>) => {
      const { modelKey, step } = action.payload;
      if (!state.entityRefineWorkflow) {
        state.entityRefineWorkflow = {};
      }
      if (!state.entityRefineWorkflow[modelKey]) {
        state.entityRefineWorkflow[modelKey] = { currentStep: 0 };
      }
      state.entityRefineWorkflow[modelKey].currentStep = step;
      state.entityRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setEntityRefineLLMSettings: (state, action: PayloadAction<LLMSettingsPayload>) => {
      const { modelKey, settings } = action.payload;
      if (!state.entityRefineWorkflow) {
        state.entityRefineWorkflow = {};
      }
      if (!state.entityRefineWorkflow[modelKey]) {
        state.entityRefineWorkflow[modelKey] = { currentStep: 0 };
      }
      state.entityRefineWorkflow[modelKey].llmSettings = settings;
      state.entityRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    clearEntityRefineWorkflow: (state, action: PayloadAction<ModelKeyPayload | undefined>) => {
      const modelKey = action?.payload?.modelKey;
      if (modelKey) {
        delete state.entityRefineWorkflow[modelKey];
      } else {
        state.entityRefineWorkflow = {};
      }
    },

    // Attribute refine workflow (legacy)
    initAttributeRefineWorkflow: (state, action: PayloadAction<ModelKeyPayload>) => {
      const { modelKey } = action.payload;
      if (!state.attributeRefineWorkflow) {
        state.attributeRefineWorkflow = {};
      }
      state.attributeRefineWorkflow[modelKey] = {
        currentStep: 0,
        step1: undefined,
        step2: undefined,
        step3: undefined,
        step4: undefined,
        llmSettings: { model: null, skipCache: false },
        lastUpdated: new Date().toISOString(),
      };
    },

    setAttributeRefineStep1: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.attributeRefineWorkflow) {
        state.attributeRefineWorkflow = {};
      }
      if (!state.attributeRefineWorkflow[modelKey]) {
        state.attributeRefineWorkflow[modelKey] = { currentStep: 0 };
      }
      state.attributeRefineWorkflow[modelKey].step1 = result;
      state.attributeRefineWorkflow[modelKey].currentStep = Math.max(
        state.attributeRefineWorkflow[modelKey].currentStep || 0,
        0
      );
      state.attributeRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setAttributeRefineStep2: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.attributeRefineWorkflow) {
        state.attributeRefineWorkflow = {};
      }
      if (!state.attributeRefineWorkflow[modelKey]) {
        state.attributeRefineWorkflow[modelKey] = { currentStep: 0 };
      }
      state.attributeRefineWorkflow[modelKey].step2 = result;
      state.attributeRefineWorkflow[modelKey].currentStep = Math.max(
        state.attributeRefineWorkflow[modelKey].currentStep || 0,
        1
      );
      state.attributeRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setAttributeRefineStep3: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.attributeRefineWorkflow) {
        state.attributeRefineWorkflow = {};
      }
      if (!state.attributeRefineWorkflow[modelKey]) {
        state.attributeRefineWorkflow[modelKey] = { currentStep: 0 };
      }
      state.attributeRefineWorkflow[modelKey].step3 = result;
      state.attributeRefineWorkflow[modelKey].currentStep = Math.max(
        state.attributeRefineWorkflow[modelKey].currentStep || 0,
        2
      );
      state.attributeRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setAttributeRefineStep4: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.attributeRefineWorkflow) {
        state.attributeRefineWorkflow = {};
      }
      if (!state.attributeRefineWorkflow[modelKey]) {
        state.attributeRefineWorkflow[modelKey] = { currentStep: 0 };
      }
      state.attributeRefineWorkflow[modelKey].step4 = result;
      state.attributeRefineWorkflow[modelKey].currentStep = 3;
      state.attributeRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setAttributeRefineCurrentStep: (state, action: PayloadAction<SetCurrentStepPayload>) => {
      const { modelKey, step } = action.payload;
      if (!state.attributeRefineWorkflow) {
        state.attributeRefineWorkflow = {};
      }
      if (!state.attributeRefineWorkflow[modelKey]) {
        state.attributeRefineWorkflow[modelKey] = { currentStep: 0 };
      }
      state.attributeRefineWorkflow[modelKey].currentStep = step;
      state.attributeRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setAttributeRefineLLMSettings: (state, action: PayloadAction<LLMSettingsPayload>) => {
      const { modelKey, settings } = action.payload;
      if (!state.attributeRefineWorkflow) {
        state.attributeRefineWorkflow = {};
      }
      if (!state.attributeRefineWorkflow[modelKey]) {
        state.attributeRefineWorkflow[modelKey] = { currentStep: 0 };
      }
      state.attributeRefineWorkflow[modelKey].llmSettings = settings;
      state.attributeRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    clearAttributeRefineWorkflow: (state, action: PayloadAction<ModelKeyPayload | undefined>) => {
      const modelKey = action?.payload?.modelKey;
      if (modelKey) {
        delete state.attributeRefineWorkflow[modelKey];
      } else {
        state.attributeRefineWorkflow = {};
      }
    },

    // Location history
    pushLocationHistory: (state, action: PayloadAction<Omit<LocationHistoryItem, 'timestamp'>>) => {
      if (!state.locationHistory) {
        state.locationHistory = { items: [], currentIndex: -1 };
      }

      const location: LocationHistoryItem = {
        ...action.payload,
        timestamp: Date.now(),
      };

      const maxItems = 10;

      if (state.locationHistory.currentIndex < state.locationHistory.items.length - 1) {
        state.locationHistory.items = state.locationHistory.items.slice(
          0,
          state.locationHistory.currentIndex + 1
        );
      }

      const lastItem = state.locationHistory.items[state.locationHistory.items.length - 1];
      const isDuplicate =
        lastItem &&
        lastItem.modelKey === location.modelKey &&
        lastItem.entityId === location.entityId &&
        lastItem.attrId === location.attrId;

      if (!isDuplicate) {
        state.locationHistory.items.push(location);

        if (state.locationHistory.items.length > maxItems) {
          state.locationHistory.items.shift();
        } else {
          state.locationHistory.currentIndex++;
        }

        state.locationHistory.currentIndex = state.locationHistory.items.length - 1;
      }
    },

    goBackLocation: (state) => {
      if (!state.locationHistory) return;
      if (state.locationHistory.currentIndex > 0) {
        state.locationHistory.currentIndex--;
      }
    },

    goForwardLocation: (state) => {
      if (!state.locationHistory) return;
      if (state.locationHistory.currentIndex < state.locationHistory.items.length - 1) {
        state.locationHistory.currentIndex++;
      }
    },

    clearLocationHistory: (state) => {
      state.locationHistory = { items: [], currentIndex: -1 };
    },

    // Exclusive groups
    addExclusiveGroup: (state, action: PayloadAction<ExclusiveGroupPayload>) => {
      const {
        edgeIds,
        targetEntityId,
        notation = 'BARKER',
        mergeAttributes = false,
        mergedAttributeName = '',
      } = action.payload;

      if (!state.modelInfo.exclusiveGroups) {
        state.modelInfo.exclusiveGroups = [];
      }

      const newGroup = {
        id: uid(10),
        edgeIds,
        targetEntityId,
        notation,
        mergeAttributes,
        mergedAttributeName,
        createdAt: new Date().toISOString(),
      };
      state.modelInfo.exclusiveGroups.push(newGroup);
      state.modelInfo.changed = true;
    },

    removeExclusiveGroup: (state, action: PayloadAction<string>) => {
      const groupId = action.payload;
      if (!state.modelInfo.exclusiveGroups) return;
      state.modelInfo.exclusiveGroups = state.modelInfo.exclusiveGroups.filter(
        (g) => g.id !== groupId
      );
      state.modelInfo.changed = true;
    },

    updateExclusiveGroup: (state, action: PayloadAction<UpdateExclusiveGroupPayload>) => {
      const { id, ...updates } = action.payload;
      if (!state.modelInfo.exclusiveGroups) return;
      state.modelInfo.exclusiveGroups = state.modelInfo.exclusiveGroups.map((g) =>
        g.id === id ? { ...g, ...updates } : g
      );
      state.modelInfo.changed = true;
    },

    updateExclusiveGroupNotation: (state, action: PayloadAction<ExclusiveNotationPayload>) => {
      const { groupId, notation } = action.payload;
      if (!state.modelInfo.exclusiveGroups) return;
      state.modelInfo.exclusiveGroups = state.modelInfo.exclusiveGroups.map((g) =>
        g.id === groupId ? { ...g, notation } : g
      );
      state.modelInfo.changed = true;
    },

    removeEdgeFromExclusiveGroups: (state, action: PayloadAction<string>) => {
      const edgeId = action.payload;
      if (!state.modelInfo.exclusiveGroups) return;

      state.modelInfo.exclusiveGroups = state.modelInfo.exclusiveGroups
        .map((group) => ({
          ...group,
          edgeIds: group.edgeIds.filter((id) => id !== edgeId),
        }))
        .filter((group) => group.edgeIds.length >= 2);
      state.modelInfo.changed = true;
    },

    updateExclusiveGroupEdgeId: (state, action: PayloadAction<EdgeIdUpdatePayload>) => {
      const { oldEdgeId, newEdgeId } = action.payload;
      if (!state.modelInfo.exclusiveGroups) return;

      state.modelInfo.exclusiveGroups = state.modelInfo.exclusiveGroups.map((group) => ({
        ...group,
        edgeIds: group.edgeIds.map((id) => (id === oldEdgeId ? newEdgeId : id)),
      }));
      state.modelInfo.changed = true;
    },

    clearExclusiveGroups: (state) => {
      state.modelInfo.exclusiveGroups = [];
    },

    // Subtype groups
    addSubtypeGroup: (state, action: PayloadAction<SubtypeGroupPayload>) => {
      const {
        edgeIds,
        sourceEntityId,
        notation = 'BARKER',
        isExclusive = true,
        isComplete = false,
        discriminator = '',
      } = action.payload;

      if (!state.modelInfo.subtypeGroups) {
        state.modelInfo.subtypeGroups = [];
      }

      const newGroup = {
        id: uid(10),
        edgeIds,
        sourceEntityId,
        notation,
        isExclusive,
        isComplete,
        discriminator,
        createdAt: new Date().toISOString(),
      };
      state.modelInfo.subtypeGroups.push(newGroup);
      state.modelInfo.changed = true;
    },

    removeSubtypeGroup: (state, action: PayloadAction<string>) => {
      const groupId = action.payload;
      if (!state.modelInfo.subtypeGroups) return;
      state.modelInfo.subtypeGroups = state.modelInfo.subtypeGroups.filter(
        (g) => g.id !== groupId
      );
      state.modelInfo.changed = true;
    },

    updateSubtypeGroup: (state, action: PayloadAction<UpdateExclusiveGroupPayload>) => {
      const { id, ...updates } = action.payload;
      if (!state.modelInfo.subtypeGroups) return;
      state.modelInfo.subtypeGroups = state.modelInfo.subtypeGroups.map((g) =>
        g.id === id ? { ...g, ...updates } : g
      );
      state.modelInfo.changed = true;
    },

    updateSubtypeGroupOptions: (state, action: PayloadAction<SubtypeOptionsPayload>) => {
      const { groupId, isExclusive, isComplete, discriminator, notation } = action.payload;
      if (!state.modelInfo.subtypeGroups) return;
      state.modelInfo.subtypeGroups = state.modelInfo.subtypeGroups.map((g) => {
        if (g.id === groupId) {
          return {
            ...g,
            ...(isExclusive !== undefined && { isExclusive }),
            ...(isComplete !== undefined && { isComplete }),
            ...(discriminator !== undefined && { discriminator }),
            ...(notation !== undefined && { notation }),
          };
        }
        return g;
      });
      state.modelInfo.changed = true;
    },

    removeEdgeFromSubtypeGroups: (state, action: PayloadAction<string>) => {
      const edgeId = action.payload;
      if (!state.modelInfo.subtypeGroups) return;

      state.modelInfo.subtypeGroups = state.modelInfo.subtypeGroups
        .map((group) => ({
          ...group,
          edgeIds: group.edgeIds.filter((id) => id !== edgeId),
        }))
        .filter((group) => group.edgeIds.length >= 2);
      state.modelInfo.changed = true;
    },

    updateSubtypeGroupEdgeId: (state, action: PayloadAction<EdgeIdUpdatePayload>) => {
      const { oldEdgeId, newEdgeId } = action.payload;
      if (!state.modelInfo.subtypeGroups) return;

      state.modelInfo.subtypeGroups = state.modelInfo.subtypeGroups.map((group) => ({
        ...group,
        edgeIds: group.edgeIds.map((id) => (id === oldEdgeId ? newEdgeId : id)),
      }));
      state.modelInfo.changed = true;
    },

    clearSubtypeGroups: (state) => {
      state.modelInfo.subtypeGroups = [];
    },
  },
});

export const {
  resetState,
  setCurrentRouteKey,
  setRelType,
  setViewMode,
  setEdgeLabelVisible,
  setHoveredEdgeId,
  setSelectionMode,
  setAutoAdjustEdges,
  setAppInitialized,
  setIsUndo,
  setIsRedo,
  setFocusInfo,
  setModelInfo,
  setActiveModelKey,
  commitNodeChanges,
  commitEdgeChanges,
  setModelChanged,
  markCurrentModelChanged,
  setEntityInfo,
  setModelInfoEdges,
  setMultiModelData,
  addMultiModelData,
  renameMultiModelData,
  renameProjectInMultiModelData,
  deleteMultiModelData,
  setItemMenu,
  setDiagramMode,
  addOrReplaceAttrs,
  addAttrs,
  replaceAttrs,
  updateModelAttrsById,
  mergeItems,
  replaceAttrItems,
  applyQualityResults,
  applyQualityResultsToEntity,
  clearAllQualityResults,
  replaceMultipleAttrItems,
  removeEdgeItem,
  deselectItems,
  updateEntityName,
  updatePhysicalName,
  toggleDetailVisibility,
  setIsDetailVisible,

  setDictionaryInfotypeLoading,
  setDictionaryInfotypeSuccess,
  setDictionaryInfotypeFailure,

  setDictionaryDomainGroupLoading,
  setDictionaryDomainGroupSuccess,
  setDictionaryDomainGroupFailure,

  setDictionaryWordsLoading,
  setDictionaryWordsSuccess,
  setDictionaryWordsFailure,

  setDictionaryTermsLoading,
  setDictionaryTermsSuccess,
  setDictionaryTermsFailure,

  setViewport,
  setIdentifierChanged,
  updateEntityPhysicalNames,

  setModelConfig,
  updateModelConfigTab,
  updateModelConfigSection,
  removeModelConfig,

  setToolbarLists,
  setLiveParticipation,
  setLiveModel,
  setLiveMdlrmdlId,
  setPersistLoaded,
  setModelDataRestored,
  setModelLoading,
  setModelLoadingProgress,
  setSaving,
  setSavingProgress,
  setApiList,
  setUserLoggingMode,

  // Analysis results
  setCoreEntityAnalysisResult,
  setRelationshipInferenceResult,
  setEntityClassificationResult,
  setCandidateKeyResult,
  setEntitySummaryResult,
  clearAnalysisResults,

  // Standard survey results
  setNamingRuleResult,
  setWordCandidateResult,
  setTermCandidateResult,
  setDomainCandidateResult,
  setProjectTermCandidateResult,
  clearProjectTermCandidateResult,

  // Legacy term workflow
  initTermCandidateWorkflow,
  setTermWorkflowStep1,
  setTermWorkflowStep2,
  setTermWorkflowStep3,
  setTermWorkflowStep4,
  setTermWorkflowStep5,
  setTermWorkflowCurrentStep,
  clearTermCandidateWorkflow,

  // Legacy refine results
  setEntityRefineResult,
  setAttributeRefineResult,
  clearEntityRefineResult,
  clearAttributeRefineResult,

  // Legacy entity refine workflow
  initEntityRefineWorkflow,
  setEntityRefineStep1,
  setEntityRefineStep2,
  setEntityRefineStep3,
  setEntityRefineStep4,
  setEntityRefineCurrentStep,
  setEntityRefineLLMSettings,
  clearEntityRefineWorkflow,

  // Legacy attribute refine workflow
  initAttributeRefineWorkflow,
  setAttributeRefineStep1,
  setAttributeRefineStep2,
  setAttributeRefineStep3,
  setAttributeRefineStep4,
  setAttributeRefineCurrentStep,
  setAttributeRefineLLMSettings,
  clearAttributeRefineWorkflow,

  // Location history
  pushLocationHistory,
  goBackLocation,
  goForwardLocation,
  clearLocationHistory,

  // Exclusive groups
  addExclusiveGroup,
  removeExclusiveGroup,
  updateExclusiveGroup,
  updateExclusiveGroupNotation,
  removeEdgeFromExclusiveGroups,
  updateExclusiveGroupEdgeId,
  clearExclusiveGroups,

  // Subtype groups
  addSubtypeGroup,
  removeSubtypeGroup,
  updateSubtypeGroup,
  updateSubtypeGroupOptions,
  removeEdgeFromSubtypeGroups,
  updateSubtypeGroupEdgeId,
  clearSubtypeGroups,
} = commonSlice.actions;

// ============================================================
// Selectors (Phase 1: 단일 진실 소스 마이그레이션 — multiModelData[activeModelKey] 직접 조회)
// ============================================================

type RootStateLite = { base: { common: CommonState } };

/**
 * 활성 모델 — multiModelData[activeModelKey].
 * Phase 1: modelInfo 와 동등한 값을 반환 (병행 운영). Phase 4 에서 modelInfo 가 제거되면 유일한 진실 소스.
 */
export const selectActiveModel = (state: RootStateLite): ModelInfo | null => {
  const key = state.base.common.activeModelKey;
  if (!key) return null;
  const models = state.base.common.multiModelData as Record<string, ModelInfo>;
  return models[key] ?? null;
};

/** 활성 모델 key 만 — 변경 빈도가 낮아 useSelector 리렌더 비용 최소화용 */
export const selectActiveModelKey = (state: RootStateLite): string | null =>
  state.base.common.activeModelKey;

export default commonSlice.reducer;
